import { FC, useEffect, useState } from 'react';
import { CustomCheckbox, CustomInput } from 'components';
import CustomAvatar from 'components/avatar/CustomAvatar';
import OutsideClickHandle from 'components/outside-click-handler/OutsideClickHandler';
import { CheckboxTickIcon, CustomersTabIcon, NavCloseIcon, ThinSearchIcon } from 'assets/icons';
import { AssignableMember } from 'containers/jobs/jobs.model';
import { selectCurrentTeam, selectUserDetails } from 'containers/app/appSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useLazyGetAssignableMembersQuery } from 'containers/jobs/api';
import AssignableMembersModal from 'containers/jobs/create-edit-job/components/AssignableMembersModal';
import { selectAssignees, updateAssignees } from '../estimatesSlice';

interface AssignEstimatesMembersProps {
  className?: string;
}

const AssignEstimatesMembers: FC<AssignEstimatesMembersProps> = () => {
  const [searchText, setSearchText] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionList, setSuggestionList] = useState<AssignableMember[]>([]);
  const [openAssignMembersModal, setOpenAssignMembersModal] = useState(false);

  const currentUser = useAppSelector(selectUserDetails);
  const currentTeam = useAppSelector(selectCurrentTeam);

  const dispatch = useAppDispatch();

  const [getAssignableTeamMemberQuery, { data: members, isSuccess }] = useLazyGetAssignableMembersQuery();
  const isSingleMemberTeam = members?.data?.teamMemberList?.length === 1;

  const assigneeList = useAppSelector(selectAssignees);

  const setAssigneeList = (list: AssignableMember[]) => {
    dispatch(updateAssignees(list));
  };

  useEffect(() => {
    if (currentTeam?.id) {
      getAssignableTeamMemberQuery(currentTeam.id.toString());
    }
  }, [getAssignableTeamMemberQuery, currentTeam]);

  useEffect(() => {
    if (isSuccess && members) {
      setSuggestionList(members.data.teamMemberList);
      const selfMember = members.data.teamMemberList.find(member => member.merchantId === currentUser.id);
      if (selfMember) {
        addToAssigneeList(selfMember);
      }
    }
  }, [isSuccess, members]);

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    setShowSuggestions(value !== '');
  };

  const addToAssigneeList = (member: AssignableMember) => {
    if (!assigneeList.find(item => item.merchantId === member.merchantId)) {
      setAssigneeList([...assigneeList, member]);
    }
  };

  const removeFromAssigneeList = (memberId: string) => {
    const updatedAssigneeList = assigneeList.filter(member => member.merchantId !== memberId);
    setAssigneeList(updatedAssigneeList);
  };

  const renderSuggestions = () => {
    const filteredList = suggestionList.filter(member => member.name.toLowerCase().includes(searchText.toLowerCase()));
    return filteredList.length > 0 ? (
      filteredList.map(member => (
        <div
          key={member.merchantId}
          onClick={e => {
            e.preventDefault();
            addToAssigneeList(member);
            handleSearchChange('');
          }}
          className="flex cursor-pointer gap-5 rounded border-b
           border-borderGray py-3 px-1 last:border-b-0 hover:bg-highlightGray">
          <CustomAvatar src={member.profilePictureUrl}>{member.name.charAt(0)}</CustomAvatar>
          <div className="text-lg font-semibold leading-5 text-headingGray">{member.name}</div>
        </div>
      ))
    ) : (
      <div className="mx-auto my-5 text-sbase font-semibold text-primaryText">No results found!</div>
    );
  };

  const renderAssigneeList = () => {
    return assigneeList.map(member => (
      <div key={member.merchantId} className="flex items-center gap-2 rounded-[10px] bg-borderGray py-1 px-2">
        <div className="text-sbase text-primaryText">
          {member.name}
          {member.merchantId === currentUser.id && <span> {'(you)'}</span>}
        </div>
        <NavCloseIcon
          className="path-fill-current path cursor-pointer text-gray-1"
          width={10}
          height={10}
          onClick={() => removeFromAssigneeList(member.merchantId)}
        />
      </div>
    ));
  };

  const handleOpenAssignMembersModal = () => {
    setOpenAssignMembersModal(true);
  };

  const handleCloseAssignMembersModal = () => {
    setOpenAssignMembersModal(false);
  };

  const handleSave = (selectedMembers: AssignableMember[]) => {
    setAssigneeList(selectedMembers);
    handleCloseAssignMembersModal();
  };

  return (
    <div className="w-full space-y-2">
      <div className="text-17px font-semibold">
        Assign team (Sold by)<span className="text-error">*</span>
      </div>
      <div className="flex items-center gap-2.5 py-1">
        <CustomCheckbox
          id="assign-estimate-to-self"
          onClick={() =>
            addToAssigneeList(members?.data?.teamMemberList?.find(member => member.merchantId === currentUser.id))
          }
          isChecked={assigneeList.some(member => member.merchantId === currentUser.id)}
          className={isSingleMemberTeam ? 'cursor-default' : ''}>
          {assigneeList.some(member => member.merchantId === currentUser.id) && <CheckboxTickIcon />}
        </CustomCheckbox>
        <label htmlFor="assign-estimate-to-self" className="text-sbase text-secondary">
          Assign to myself
        </label>
      </div>

      <div className="flex items-start justify-between rounded bg-secondaryBtn px-5 py-2.5">
        <div className="relative flex grow items-start">
          <div className="mr-2 flex h-[26px] items-center justify-center">
            <ThinSearchIcon />
          </div>
          <div className="flex flex-wrap gap-2">
            {renderAssigneeList()}
            <CustomInput
              id="search-member"
              value={searchText}
              onChange={handleSearchChange}
              placeholder={assigneeList.length > 0 ? 'Search another member' : 'Search a member'}
              className="h-6 p-0 outline-none"
            />
          </div>

          {showSuggestions && (
            <OutsideClickHandle onOutsideClick={() => setShowSuggestions(false)}>
              <div
                className="absolute top-full -left-5 z-20 w-full 
                rounded border border-borderGray bg-primary px-5 py-2 shadow-md">
                {renderSuggestions()}
              </div>
            </OutsideClickHandle>
          )}
        </div>

        <div
          className="flex shrink-0 cursor-pointer items-center gap-2"
          role="presentation"
          onClick={handleOpenAssignMembersModal}>
          <CustomersTabIcon
            className="path-stroke-current path-fill-current path text-secondary"
            width={24}
            height={24}
          />
          <div className="text-17px font-semibold text-secondary">View all members</div>
        </div>
      </div>

      {openAssignMembersModal && (
        <AssignableMembersModal
          list={suggestionList}
          onClose={handleCloseAssignMembersModal}
          selectedMembers={assigneeList}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default AssignEstimatesMembers;
